import React from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";
import { Select, MenuItem } from "@mui/material";
import type Stripe from "stripe";
import moment from "moment";

import { TextContent } from "js/Components/TextContent";
import { RecurringPlanDefinition } from "common/stripeProducts";

import { calculatePriceWithPromo } from "../../utils/prices";
import { TermsOfUseAndRefundPolicy } from "../TermsOfUseAndRefundPolicy/TermsOfUseAndRefundPolicy";

const Badge = styled.div`
    background: #7fb314;
    color: white;
    font-size: 10px;
    letter-spacing: 0.5px;
    padding: 2px 8px;
    display: inline-block;
    margin-left: 30px;
    font-weight: 600;
    position: absolute;
    top: calc(50% - 11px);
    right: 30px;
`;

interface BillingIntervalMenuProps {
    isTrial: boolean;
    billingInterval: "month" | "year";
    prices: { month: RecurringPlanDefinition, year: RecurringPlanDefinition };
    showBestValueBadge: boolean;
    promo: Stripe.PromotionCode | null;
    availableReferralCredit: number;
    taxAmount?: number;
    onChange: (billingInterval: "month" | "year") => void;
}

export function BillingIntervalMenu(props: BillingIntervalMenuProps) {
    const { t } = useTranslation();
    const { isTrial, billingInterval, prices, showBestValueBadge, promo, availableReferralCredit, taxAmount, onChange } = props;

    const isAnnual = billingInterval === "year";

    const currentPrice = prices[billingInterval].amount / 100;
    const currentPromoAppliedPrice = calculatePriceWithPromo(currentPrice, promo);

    const annualCostAtAnnualRate = prices.year.amount / 100;
    const annualCostAtMonthlyRate = prices.month.amount / 100;

    const dueToday = isTrial ? 0 : Math.max(currentPromoAppliedPrice - (availableReferralCredit / 100), 0);

    const taxString = taxAmount ? t(" plus tax") : "";

    const trialExpirationDate = moment().add(14, "days").format("MMMM Do, YYYY");
    const nextBillingDate = isTrial ? trialExpirationDate : isAnnual
        ? moment().add(1, "year").format("MMMM Do, YYYY")
        : moment().add(1, "month").format("MMMM Do, YYYY");

    return (<>
        <TextContent body bold margin="0 0 18px 0">{t("Choose Plan")}</TextContent>

        <Select
            SelectDisplayProps={{ style: { textTransform: "capitalize" } }}
            style={{ width: "100%" }}
            variant="outlined"
            value={billingInterval}
            onChange={event => onChange(event.target.value as "month" | "year")}
        >
            <MenuItem value="year">
                {t("Annual Plan - ${{price}}/month", { price: annualCostAtAnnualRate / 12 })}
                {showBestValueBadge && <Badge>{t("Best Value")}</Badge>}
            </MenuItem>
            <MenuItem value="month">{t("Monthly Plan - ${{price}}/month", { price: annualCostAtMonthlyRate })}</MenuItem>
        </Select>

        <TextContent largeLabel margin="10px 0 0 0">
            <Trans
                i18nKey="You will be charged <bold>${{amount}}{{tax}}</bold> today. Your next bill will be due on <bold>{{date}}</bold>. <terms />"
                values={{ amount: dueToday.toFixed(2), tax: dueToday > 0 ? taxString : "", date: nextBillingDate }}
                components={{ bold: <strong />, terms: <TermsOfUseAndRefundPolicy /> }}
            />
        </TextContent>
    </>);
}
